import React from "react";
import _ from "lodash";

import IMeta from "models/IMeta";
import SearchInput from "components/SearchInput";
import SelectedFilters from "components/SelectedFilters";

interface IProps {
  onSearch: (meta: IMeta) => void;
  meta: IMeta;
  [key: string]: any;
}

interface IState {
  filter: any;
  initialized: boolean;
  inputValue: number | string;
  filterOpened: boolean;
}

/**
 *  WrapperComponent should have two static methods: getInputQuery, getFilterQuery
 */

const withFilters = ({
  placeholder,
  pageName = "",
  fixedFilters = [] as string[]
}) => WrappedComponent => {
  return class FiltersContainer extends React.Component<IProps, IState> {
    public constructor(props) {
      super(props);

      this.state = {
        filterOpened: false,
        initialized: false,
        inputValue: "",
        filter: {}
      };
    }

    public componentDidMount() {
      this.initSearch();
    }

    public static getDerivedStateFromProps(props, state) {
      const {
        meta: { filter, inputValue }
      } = props;
      if (!state.initialized) {
        const savedSettings = JSON.parse(
          localStorage.getItem("customSettings") || "{}"
        );
        const filterOpened = _.get(
          savedSettings,
          `${pageName}.filterOpened`,
          false
        );

        if (filter) {
          return {
            filter: JSON.parse(filter),
            filterOpened,
            initialized: true
          };
        } else if (inputValue) {
          return {
            initialized: true,
            filterOpened,
            inputValue
          };
        }
      }

      return null;
    }

    private onSearch = _.debounce(
      newMeta =>
        this.props.onSearch({
          ...this.props.meta,
          currentPage: 0,
          inputValue: "",
          filter: null,
          ...newMeta
        }),
      500
    );

    public handleSearch = e => {
      const { value: inputValue } = e.target;
      const searchString = WrappedComponent.getInputQuery(inputValue);

      this.setState({ inputValue, filter: {} });
      this.onSearch({ searchString, inputValue });
    };

    public handleFilterChange = (value, name) => {
      this.setState(prevState => ({
        filter: _.set(prevState.filter, name, value)
      }));
    };

    public toggleFilter = () => {
      const filterOpened = this.state.filterOpened;
      const savedSettings = JSON.parse(
        localStorage.getItem("customSettings") || "{}"
      );
      _.set(savedSettings, `${pageName}.filterOpened`, !filterOpened);
      localStorage.setItem("customSettings", JSON.stringify(savedSettings));

      this.setState({
        filterOpened: !filterOpened
      });
    };

    public initSearch = () => {
      const searchString = WrappedComponent.getFilterQuery(this.state.filter);
      searchString && this.setState({ inputValue: "" });

      this.onSearch({
        searchString,
        filter: JSON.stringify(this.state.filter),
        ...this.props.meta
      });
    };

    public generateFilter = ({ newFilter = null } = {}) => {
      let filter = newFilter || this.state.filter;
      filter = _.pickBy(filter, _.identity);
      const searchString = WrappedComponent.getFilterQuery(filter);

      this.setState({ inputValue: "", filter });
      this.onSearch({
        searchString,
        filter: JSON.stringify(filter)
      });
    };

    private resetFilter = () => {
      const { meta } = this.props;
      this.setState({ inputValue: "", filter: {} });
      this.props.onSearch({ ...meta, filter: "{}", searchString: "" });
    };

    private onRemoveFilter = key => {
      const filter = this.state.filter;
      delete filter[key];
      this.setState({ filter }, () => this.generateFilter({}));
    };

    public render() {
      const { inputValue, filterOpened, filter, initialized } = this.state;
      const previousFilter = JSON.parse(_.get(this.props, "meta.filter", "{}"));
      const isFilterEmpty = _.every(previousFilter, _.isEmpty);
      const showSelectedFilters = !isFilterEmpty && initialized;

      return (
        <>
          <SearchInput
            value={filterOpened ? "" : inputValue}
            placeholder={placeholder}
            onChange={this.handleSearch}
            active={filterOpened}
            toggleFilter={this.toggleFilter}
            advancedSearch={
              <WrappedComponent
                filter={filter}
                handleFilterChange={this.handleFilterChange}
                generateFilter={this.generateFilter}
                resetFilter={this.resetFilter}
                {...this.props}
              />
            }
          />
          {showSelectedFilters && (
            <SelectedFilters
              filter={previousFilter}
              onRemove={this.onRemoveFilter}
              fixedFilters={fixedFilters}
              filterOpened={filterOpened}
            />
          )}
        </>
      );
    }
  };
};

export default withFilters;
